import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import NewCase from "@/pages/NewCase";
import Overview from "@/pages/Overview";
import RestaurantProfile from "@/pages/RestaurantProfile";
import HowItWorks from "@/pages/HowItWorks";
import CaseResult from "@/pages/CaseResult";
import History from "@/pages/History";
import PublicReply from "@/pages/PublicReply";
import Stores from "@/pages/Stores";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Privacy from "@/pages/Privacy";
import Landing from "@/pages/Landing";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import CompleteClientInfo from "@/components/CompleteClientInfo";

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-amber-500 border-t-transparent" />
  </div>
);

function Protected({ children, admin }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <Loading />;
  if (!user) return pathname === "/" ? <Landing /> : <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (user.needs_client_info) return <CompleteClientInfo />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/cadastro" element={<Login mode="register" />} />
      <Route path="/privacidade" element={<Privacy />} />
      <Route path="/esqueci-senha" element={<ForgotPassword />} />
      <Route path="/redefinir-senha" element={<ResetPassword />} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/" element={<Overview />} />
        <Route path="/restaurante" element={<RestaurantProfile />} />
        <Route path="/como-funciona" element={<HowItWorks />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/nova-analise" element={<NewCase />} />
        <Route path="/recursos" element={<History />} />
        <Route path="/recursos/:id" element={<CaseResult />} />
        <Route path="/respostas" element={<PublicReply />} />
        <Route path="/lojas" element={<Stores />} />
        <Route path="/configuracoes" element={<Settings />} />
        <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
